import { Menu, MenuItem, MenuSubmenu } from "@showitinc/showit-wc";
import React from "react";
import styles from "./RightClickMenu.module.css";
import RightClickMenuProvider from "./RightClickMenuProvider";

/**
 * Complete right-click context menu component
 * Combines all menu components into a cohesive interface
 * @param {Object} props
 * @param {import("@/services/ContextDetectionService").MenuState|null} props.menuState - Menu state from context detection
 * @param {Function} props.onClose - Callback to close the menu
 * @param {Function} [props.onSelect] - Callback when a menu action is selected
 * @param {import("./configs/BaseRightClickMenuConfig").default|null} props.menuConfig - Menu configuration instance
 * @param {Object} [props.additionalContext] - Extra context passed to menu config
 * @param {string} [props.className] - Additional CSS class
 */
const RightClickMenu = ({
  menuState,
  onClose,
  onSelect,
  menuConfig,
  additionalContext = {},
  className = "",
}) => {
  if (!menuState || !menuConfig) {
    return null;
  }

  const context = {
    entityType: menuState.entityType,
    entity: menuState.entity,
    context: menuState.context,
    position: menuState.position,
    ...additionalContext,
  };

  const menuItems = menuConfig.getMenuItems(context, additionalContext);

  const handleSelect = (action) => {
    if (onSelect) {
      onSelect(action, {
        layout: menuState.context?.layout,
        otherLayout: menuState.context?.otherLayout,
        canvasPosition: menuState.position,
        onClose,
        entity: menuState.entity,
        context: menuState.context,
      });
    }
  };

  return (
    <RightClickMenuProvider
      menuState={menuState}
      onClose={onClose}
      className={`${styles["right-click-menu-container"]} ${className}`}
    >
      <Menu variant="icon-hotkey" className={styles["right-click-menu"]}>
        {menuItems.map((item, index) => {
          if (item.type === "divider") {
            return <MenuItem key={`divider-${item.order || index}`} divider />;
          }

          if (item.type === "submenu") {
            return (
              <MenuSubmenu
                key={`submenu-${item.label || index}`}
                icon={item.icon}
                label={item.label}
                disabled={item.disabled || false}
              >
                {item.items?.map((subItem, subIndex) => {
                  if (subItem.type === "divider") {
                    return <MenuItem key={`divider-${subItem.action || subIndex}`} divider />;
                  }
                  return (
                    <MenuItem
                      key={`item-${subItem.label || subIndex}`}
                      onClick={() => handleSelect(subItem.action)}
                      disabled={item.disabled || false}
                      hotkey={subItem.hotkey}
                      icon={subItem.icon}
                      className={styles["menu-item"]}
                    >
                      {subItem.label}
                    </MenuItem>
                  );
                })}
              </MenuSubmenu>
            );
          }

          return (
            <MenuItem
              key={item.action || `item-${item.label || index}`}
              onClick={item.disabled ? undefined : () => handleSelect(item.action)}
              hotkey={item.hotkey}
              icon={item.icon}
              disabled={item.disabled || false}
              className={`${styles["menu-item"]} ${item.className || ""}`}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </Menu>
    </RightClickMenuProvider>
  );
};

export default RightClickMenu;
